import React, { useState, useEffect } from 'react';
import '../styles/LoadingAnecdotes.scss';

const generalAnecdotes = [
  "Shakespeare wrote 'Macbeth' to impress King James I, who was fascinated by witchcraft.",
  "The word 'robot' first appeared in a 1920 play by Karel Čapek.",
  "Emily Dickinson published only 10 poems during her lifetime.",
  "James Joyce's 'Ulysses' takes place entirely on June 16, 1904. That date is now commemorated annually as “Bloomsday”.",
  "The entire plot of 'Waiting for Godot' is... waiting.",
  "William Wordsworth was known for his love of long walks in the Lake District in the UK. He has been estimated to have walked 175,000 miles in his lifetime.",
  "T.S. Eliot had a day job as a bank clerk.",
  "In 1926, Agatha Christie vanished for 11 days, sparking a nationwide search. She was later found at a hotel, claiming amnesia. Many think it was a publicity stunt—or revenge on her cheating husband.",
  "The Japanese haiku was originally part of a longer poem called renga.",
  "Jane Austen never married, despite writing famous romance novels.",
  "Leo Tolstoy renounced his aristocratic wealth later in life and tried to give away his earnings from Anna Karenina and War and Peace. His wife strongly opposed this, which caused serious marital tensions.",
  "James Joyce’s handwriting was so bad that his wife, Nora Barnacle, once threw one of his drafts into the fire, thinking it was just scribbles.",
  "Dante Alighieri, in The Divine Comedy, placed real political enemies—including Pope Boniface VIII—into Hell while they were still alive. Talk about medieval subtweets!",
  "After frequenting the Sloppy Joe’s Bar in Key West, Ernest Hemingway decided the urinal was rightfully his since he had spent so much money there. He took it home, where it became a fountain in his yard.",
  "Charles Dickens had a pet raven named Grip, which inspired Edgar Allan Poe’s famous poem The Raven.",
  "Charlotte, Emily, and Anne Brontë first published under the names Currer, Ellis, and Acton Bell because women writers weren’t taken seriously in the 19th century.",
  "Leo Tolstoy thought King Lear was so bad he wrote a whole essay about how much he hated it. George Orwell later wrote an essay criticizing Tolstoy’s essay.",
  "Dr. Seuss’s publisher bet him he couldn’t write a book using just 50 different words. The result? Green Eggs and Ham.",
  "After Les Misérables was published, Victor Hugo sent his publisher a telegram containing only “?” to ask how it was selling. The publisher replied with “!”, meaning sales were great.",
  "Oscar Wilde's last words were reported to be 'Either that wallpaper goes, or I do.'",
  "Tolkien was drafting The Lord of the Rings when his typewriter lost the letter 'E'. Rather than replace it, he continued typing and handwrote every missing 'E' into the manuscript.",
    ];

const leavingCertAnecdotes = [
  // "The 'Holy Certificate': In the early days of the Leaving Cert, it was so highly regarded that some families would display their children's results on the mantelpiece alongside holy statues and wedding photos.",
  // "Latin Lovers: Up until the 1970s, Latin was a compulsory subject for those wishing to study many university courses – meaning plenty of Irish students could recite Carpe Diem without actually knowing the meaning of 'seize the day'.",
  // "Biology Blunder: In 2014, the Biology exam became infamous for including a question about how plants communicate. Cue confusion and a lot of students trying to figure out if daffodils text each other.",
  // "In 2009, a school in Drogheda handed out the wrong English paper in the Leaving Cert. The exam had to be rescheduled, and students got an extra day to cram or panic.",
  // "The 'Maths Paper 2' Crisis: In 2019, Leaving Cert Maths Paper 2 had students panicking when questions turned out to be tougher than expected. The collective despair trended online, with memes taking over Irish Twitter.",
  // "How Many Points Is That?: The points system wasn't introduced until 1977. Before that, students just passed or failed – hence the frequent mentions among older generations about how many 'honours' someone received.",
  // "Essay Panic: In 2007, students were baffled when the English Paper 1 essay question seemed unusually vague. The question 'Write a personal essay' led to thousands wondering, Personal about what?",

];

function LoadingAnecdotes({ isUKUser = false }) {
  // Determine which list of anecdotes to use
  const anecdotesToUse = isUKUser ? generalAnecdotes : [...generalAnecdotes, ...leavingCertAnecdotes];

  const getRandomIndex = () => Math.floor(Math.random() * anecdotesToUse.length);
  const [currentAnecdoteIndex, setCurrentAnecdoteIndex] = useState(getRandomIndex());
  const [usedAnecdoteIndices, setUsedAnecdoteIndices] = useState(new Set([currentAnecdoteIndex]));

  useEffect(() => {
    // Prevent interval setup if there are no anecdotes to display
    if (anecdotesToUse.length === 0) return;

    const interval = setInterval(() => {
      // Get all unused anecdote indices from the relevant list
      const availableIndices = Array.from(
        { length: anecdotesToUse.length },
        (_, i) => i
      ).filter(i => !usedAnecdoteIndices.has(i));

      let newIndex;
      if (availableIndices.length === 0) {
        // All anecdotes have been shown. Reset and pick a new one, ensuring it's not the same as the current one.
        // Handle the edge case where there's only one anecdote.
        if (anecdotesToUse.length <= 1) {
          newIndex = 0; // Or handle as appropriate if length is 0 (though checked above)
          setUsedAnecdoteIndices(new Set(anecdotesToUse.length === 1 ? [0] : []));
        } else {
           const possibleIndices = anecdotesToUse
              .map((_, i) => i)
              .filter(i => i !== currentAnecdoteIndex);
           newIndex = possibleIndices[Math.floor(Math.random() * possibleIndices.length)];
           setUsedAnecdoteIndices(new Set([newIndex])); // Reset used set with the new index
        }
      } else {
        // Pick a random index from the available (unused) ones
        const randomIndexInAvailable = Math.floor(Math.random() * availableIndices.length);
        newIndex = availableIndices[randomIndexInAvailable];
        setUsedAnecdoteIndices(prev => new Set([...prev, newIndex])); // Add new index to used set
      }
      setCurrentAnecdoteIndex(newIndex);

    }, 8000); // Change anecdote every 8 seconds

    return () => clearInterval(interval);
    // Update dependencies: anecdotesToUse list and the current/used indices
    // Reset state when anecdotesToUse changes (e.g. isUKUser prop changes)
  }, [currentAnecdoteIndex, usedAnecdoteIndices, anecdotesToUse]);

  // Effect to reset state if isUKUser changes, ensuring we start fresh with the correct list
  useEffect(() => {
      const initialIndex = getRandomIndex();
      setCurrentAnecdoteIndex(initialIndex);
      setUsedAnecdoteIndices(new Set([initialIndex]));
  }, [isUKUser]); // Rerun only when isUKUser changes

  // Handle edge case where anecdotesToUse might be empty
  if (anecdotesToUse.length === 0) {
    return (
        <div className="loading-overlay">
          <div className="loading-card">
            <div className="spinner-container">
              <div className="loading-spinner"></div>
            </div>
            <h2 className="loading-title">
              Loading...
            </h2>
          </div>
        </div>
      );
  }

  return (
    <div className="loading-overlay">
      <div className="loading-card">
        <div className="spinner-container">
          <div className="loading-spinner"></div>
        </div>
        <h2 className="loading-title">
          Loading...
        </h2>
        <h3 className="did-you-know">
          Did you know?
        </h3>
        <p className="anecdote-text">
          {anecdotesToUse[currentAnecdoteIndex]}
        </p>
      </div>
    </div>
  );
}

export default LoadingAnecdotes; 